import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import { getStaffActivity } from "@/lib/api/admin";
import type { StaffActivityLog } from "@/lib/api/types";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/Spinner";
import { Pagination } from "@/components/Pagination";
import { formatDateTime } from "@/lib/utils/date";
import { ArrowLeft, ChevronRight } from "lucide-react";

const PAGE_SIZE = 20;

// Mirrors ActivityTab.tsx's ACTION_CONFIG labels; unlisted actions fall back to raw text.
const ACTION_LABELS: Record<string, string> = {
  item_claimed: "Item claimed",
  point_reward_redeemed: "Point reward redeemed",
  stamp_earned: "Stamp earned",
  stamp_reward_redeemed: "Stamp reward redeemed",
  points_earned: "Points earned",
  points_spent: "Points spent",
  event_checkin: "Event self check-in",
  event_inviter_rewarded: "Event guest check-in",
  referral_invitee_rewarded: "Referral invitee bonus",
  referral_inviter_rewarded: "Referral inviter bonus",
  store_referral_invitee_rewarded: "Store referral invitee bonus",
  store_referral_inviter_rewarded: "Store referral inviter bonus",
  bundle_items_claimed: "Bundle claimed",
  points_expired: "Points expired",
  stamps_expired: "Stamps expired",
  signup_bonus: "Welcome bonus",
  appointment_checkin: "Appointment check-in",
  appointment_no_show_penalty: "No-show penalty",
  admin_balance_adjusted: "Balance adjusted by support",
};

function formatActionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action.replace(/_/g, " ");
}

// ─── Detail helpers ───────────────────────────────────────────────────────────

function formatCents(cents: unknown): string {
  if (typeof cents !== "number") return "—";
  return `$${(cents / 100).toFixed(2)}`;
}

type DetailRow = {
  label: string;
  value: string;
};

function buildDetailRows(a: StaffActivityLog): DetailRow[] {
  const d = a.details ?? {};
  const rows: DetailRow[] = [];

  switch (a.action) {
    case "item_claimed":
      if (d.menu_item_name) rows.push({ label: "Item", value: String(d.menu_item_name) });
      if (d.price_cents != null)
        rows.push({ label: "Price", value: formatCents(d.price_cents as number) });
      if (d.savings_cents != null)
        rows.push({ label: "Savings", value: formatCents(d.savings_cents as number) });
      if (d.discount_percent != null)
        rows.push({
          label: "Discount",
          value: `${Math.round(d.discount_percent as number)}%`,
        });
      if (d.menu_item_id) rows.push({ label: "Item ID", value: String(d.menu_item_id) });
      break;

    case "point_reward_redeemed":
      if (d.point_reward_name) rows.push({ label: "Reward", value: String(d.point_reward_name) });
      if (d.point_reward_id) rows.push({ label: "Reward ID", value: String(d.point_reward_id) });
      break;

    case "stamp_reward_redeemed":
      if (d.stamp_reward_name)
        rows.push({ label: "Stamp Reward", value: String(d.stamp_reward_name) });
      if (d.stamp_reward_id)
        rows.push({ label: "Stamp Reward ID", value: String(d.stamp_reward_id) });
      break;

    case "event_checkin":
      if (d.event_title) rows.push({ label: "Event", value: String(d.event_title) });
      if (d.event_id) rows.push({ label: "Event ID", value: String(d.event_id) });
      break;

    default:
      for (const [k, v] of Object.entries(d)) {
        rows.push({ label: k.replace(/_/g, " "), value: String(v) });
      }
  }

  return rows;
}

// ─── Detail view ─────────────────────────────────────────────────────────────

function ActivityDetail({ log, onBack }: { log: StaffActivityLog; onBack: () => void }) {
  const navigate = useNavigate();
  const rows = buildDetailRows(log);

  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" className="-ml-2" onClick={onBack}>
        <ArrowLeft className="size-4 mr-1" /> Back
      </Button>

      <div className="space-y-3">
        {/* Action type */}
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-0.5">Action</p>
          <p className="font-semibold">{formatActionLabel(log.action)}</p>
        </div>

        {/* Customer */}
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-0.5">Customer</p>
          {log.client_name ? (
            <p
              className="text-sm cursor-pointer hover:underline text-foreground"
              onClick={() => void navigate(`/users/${log.client_uid}`)}
            >
              {log.client_name}
            </p>
          ) : (
            <p className="text-sm italic text-muted-foreground">User Removed</p>
          )}
          <p className="text-xs font-mono text-muted-foreground">{log.client_uid}</p>
        </div>

        {/* Store */}
        {log.store_name && (
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-0.5">Store</p>
            <p className="text-sm">{log.store_name}</p>
          </div>
        )}

        {/* Points / stamps */}
        {(log.points_change !== 0 || log.stamps_change !== 0) && (
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-0.5">Changes</p>
            <div className="flex gap-3">
              {log.points_change !== 0 && (
                <span
                  className={`text-sm font-medium ${log.points_change > 0 ? "text-green-600" : "text-red-600"}`}
                >
                  {log.points_change > 0 ? "+" : ""}
                  {log.points_change} pts
                </span>
              )}
              {log.stamps_change !== 0 && (
                <span
                  className={`text-sm font-medium ${log.stamps_change > 0 ? "text-green-600" : "text-red-600"}`}
                >
                  {log.stamps_change > 0 ? "+" : ""}
                  {log.stamps_change} stamps
                </span>
              )}
            </div>
          </div>
        )}

        {/* Action-specific detail rows */}
        {rows.length > 0 && (
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Details</p>
            <dl className="space-y-1">
              {rows.map((r) => (
                <div key={r.label} className="flex justify-between gap-4 text-sm">
                  <dt className="text-muted-foreground capitalize shrink-0">{r.label}</dt>
                  <dd className="font-mono text-xs truncate text-right">{r.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        {/* Timestamp */}
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-0.5">Time</p>
          <p className="text-sm">{formatDateTime(log.created_at)}</p>
        </div>
      </div>
    </div>
  );
}

// ─── Main dialog ──────────────────────────────────────────────────────────────

type StaffActivityDialogProps = {
  staff: { id: string; name: string } | null;
  onClose: () => void;
};

export function StaffActivityDialog({ staff, onClose }: StaffActivityDialogProps) {
  const user = useAdminAuth();
  const navigate = useNavigate();
  const [offset, setOffset] = useState(0);
  const [selectedLog, setSelectedLog] = useState<StaffActivityLog | null>(null);

  const { data: activity } = useQuery({
    queryKey: ["staff-activity", staff?.id, offset],
    queryFn: () => getStaffActivity(user!, staff!.id, { limit: PAGE_SIZE, offset }),
    enabled: !!user && !!staff,
  });

  function handleOpenChange(open: boolean) {
    if (!open) {
      setOffset(0);
      setSelectedLog(null);
      onClose();
    }
  }

  return (
    <Dialog open={!!staff} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {selectedLog ? "Scan Activity Detail" : `${staff?.name}'s Scanner Activity`}
          </DialogTitle>
        </DialogHeader>

        {selectedLog ? (
          <ActivityDetail log={selectedLog} onBack={() => setSelectedLog(null)} />
        ) : activity ? (
          <>
            {activity.data.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                No scanner activity yet.
              </p>
            ) : (
              <ul className="divide-y max-h-[60vh] overflow-y-auto">
                {activity.data.map((a) => (
                  <li
                    key={a.id}
                    className="flex items-start justify-between py-2 text-sm gap-2 cursor-pointer hover:bg-muted/50 rounded-md px-1 -mx-1 transition-colors"
                    onClick={() => setSelectedLog(a)}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-medium">{formatActionLabel(a.action)}</span>
                        {a.points_change !== 0 && (
                          <span
                            className={`text-xs ${a.points_change > 0 ? "text-green-600" : "text-red-600"}`}
                          >
                            {a.points_change > 0 ? "+" : ""}
                            {a.points_change} pts
                          </span>
                        )}
                        {a.stamps_change !== 0 && (
                          <span
                            className={`text-xs ${a.stamps_change > 0 ? "text-green-600" : "text-red-600"}`}
                          >
                            {a.stamps_change > 0 ? "+" : ""}
                            {a.stamps_change} stamps
                          </span>
                        )}
                      </div>
                      {a.client_name ? (
                        <p
                          className="text-xs text-muted-foreground truncate hover:underline"
                          onClick={(e) => {
                            e.stopPropagation();
                            void navigate(`/users/${a.client_uid}`);
                          }}
                        >
                          {a.client_name}
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground italic">User Removed</p>
                      )}
                      {a.store_name && (
                        <p className="text-xs text-muted-foreground truncate">{a.store_name}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <span className="text-xs text-muted-foreground">
                        {formatDateTime(a.created_at)}
                      </span>
                      <ChevronRight className="size-3 text-muted-foreground" />
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {activity.data.length > 0 && (
              <div className="mt-4">
                <Pagination
                  offset={offset}
                  limit={PAGE_SIZE}
                  total={activity.total}
                  onOffsetChange={setOffset}
                />
              </div>
            )}
          </>
        ) : (
          <div className="flex justify-center py-6">
            <Spinner className="size-5 border-primary" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
